import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureWorkbench from "@/components/SignatureWorkbench";

const API = process.env.REACT_APP_BACKEND_URL;

/** Public signing page — no login. Full-screen DigiSigner-style editor:
 * click the document to place signature, initials, text, date, checkmarks. */
export default function SignDocumentPage() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [pages, setPages] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/sign-requests/${token}`)
      .then((r) => setReq(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This signing link is invalid."));
    axios.get(`${API}/api/sign-requests/${token}/pages`)
      .then((r) => setPages(r.data.pages || []))
      .catch(() => setPages([]));
  }, [token]);

  const submit = async (payload) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/sign-requests/${token}/sign`, payload);
      setDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (error) return <Shell><p className="text-red-600 font-semibold text-center py-16" data-testid="sign-error">{error}</p></Shell>;
  if (!req || pages === null) return <Shell><p className="text-slate-400 text-center py-16">Loading document…</p></Shell>;
  if (req.status === "waiting") {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="sign-waiting">
          <div className="text-5xl mb-4">⏳</div>
          <h2 className="text-xl font-bold">Not your turn yet</h2>
          <p className="text-slate-500 mt-2">
            This document is signed in order — we're waiting for <strong>{req.waiting_on}</strong> to sign first.
            You'll receive an email when it's your turn.
          </p>
        </div>
      </Shell>
    );
  }
  if (done || req.status === "signed") {
    const waiting = (req.others || []).filter((o) => o.status !== "signed");
    return (
      <Shell>
        <div className="text-center py-16" data-testid="sign-done">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-bold">Document signed</h2>
          <p className="text-slate-500 mt-2">Thank you — {req.from_business} has been notified.</p>
          {waiting.length > 0 && (
            <p className="text-slate-400 text-sm mt-2" data-testid="sign-waiting-others">
              Still waiting on: {waiting.map((o) => o.name).join(", ")}
            </p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <SignatureWorkbench
      pages={pages}
      docUrl={`${API}/api/sign-requests/${token}/download`}
      defaultName={req.recipient_name || ""}
      placeholders={req.placeholders || []}
      allowEdits={false}
      busy={busy}
      onSubmit={submit}
      title={`${req.file_name} — ${req.from_business}`}
    />
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-6">{children}</div>
    </div>
  );
}
